import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import { toast } from '@/components/toasts/toast.helpers'
import { getSinglePromptTestResults } from '@/reducers/prompts/prompts.actions'
import { getEvidenceCallId } from '@/utils/promptFeedback.helpers'

/**
 * Hook to manage prompt test execution state
 * @param {object|null} prompt - The current prompt object
 * @param {Array} allEvidence - All evidence items for the task
 * @param {function} getCurrentPromptValues - Function to get current prompt values (from usePromptEditing)
 * @param {string|null} feedbackText - The feedback text from the task
 * @param {string|null} expectedScore - The expected score from the task
 * @param {string|null} taskId - The task ID (for resetting state)
 * @returns {{
 *   testLoading: object,
 *   runAllLoading: boolean,
 *   allTestResults: object,
 *   expandedTests: object,
 *   setExpandedTests: function,
 *   handleRunSingleTest: function,
 *   handleRunAllTests: function,
 *   manualTestCases: Array,
 *   addManualTestCase: function,
 *   removeManualTestCase: function,
 *   handleRunManualTest: function
 * }}
 */
export const usePromptTesting = (
  prompt,
  allEvidence,
  getCurrentPromptValues,
  feedbackText,
  expectedScore,
  taskId
) => {
  const dispatch = useDispatch()

  const [testLoading, setTestLoading] = useState({})
  const [runAllLoading, setRunAllLoading] = useState(false)
  const [allTestResults, setAllTestResults] = useState({})
  const [expandedTests, setExpandedTests] = useState({})
  // Manual test cases are session-only, not persisted to task
  const [manualTestCases, setManualTestCases] = useState([])

  useEffect(() => {
    setAllTestResults({})
    setExpandedTests({})
    setTestLoading({})
    setRunAllLoading(false)
    setManualTestCases([])
  }, [taskId])

  const addManualTestCase = useCallback(({ callId, expectedResult, callType, description }) => {
    const newTestCase = {
      id: `manual-${uuidv4()}`,
      call_id: callType === 'call' ? callId : null,
      message_id: callType === 'message' ? callId : null,
      call_type: callType,
      expected_result: expectedResult,
      description,
      isManual: true,
    }
    setManualTestCases((prev) => [...prev, newTestCase])
  }, [])

  const removeManualTestCase = useCallback((testCaseId) => {
    setManualTestCases((prev) => prev.filter((tc) => tc.id !== testCaseId))
    setAllTestResults((prev) => {
      const updated = { ...prev }
      delete updated[testCaseId]
      return updated
    })
    setExpandedTests((prev) => {
      const updated = { ...prev }
      delete updated[testCaseId]
      return updated
    })
  }, [])

  const handleRunTest = useCallback(
    async (callId, expectedResponse, callType = 'call') => {
      if (!prompt) {
        return null
      }

      const currentValues = getCurrentPromptValues()
      const result = await dispatch(
        getSinglePromptTestResults(
          prompt.id,
          {
            user_prompt: currentValues.user_prompt,
            system_prompt: currentValues.system_prompt,
            // Send only the dropdown-driven llm_model_id (the backend's source of
            // truth). Previously we also sent `prompt.model`, a stale string copied
            // from the original Redux prompt that never tracked the dropdown, so the
            // test ran against the wrong model.
            llm_model_id: currentValues.llm_model_id,
            side: currentValues.side,
            first_x_minutes: currentValues.first_x_minutes || null,
            last_x_minutes: currentValues.last_x_minutes || null,
            include_timestamps: currentValues.include_timestamps,
            silence_threshold_seconds: currentValues.silence_threshold_seconds || null,
            feature: 'qa_copilot_test',
            inputs: { call_type: callType },
          },
          expectedResponse,
          callId,
          feedbackText || ''
        )
      )
      return result
    },
    [dispatch, prompt, getCurrentPromptValues, feedbackText]
  )

  const executeTestWithTracking = useCallback(
    async (testKey, callId, expectedResponse, callType = 'call') => {
      if (!prompt) {
        return
      }

      setTestLoading((prev) => ({ ...prev, [testKey]: true }))
      try {
        const result = await handleRunTest(callId, expectedResponse, callType)
        if (result) {
          setAllTestResults((prev) => ({ ...prev, [testKey]: result }))
          setExpandedTests((prev) => ({ ...prev, [testKey]: true }))
        }
      } finally {
        setTestLoading((prev) => ({ ...prev, [testKey]: false }))
      }
    },
    [prompt, handleRunTest]
  )

  const handleRunSingleTest = useCallback(
    (evidenceItem, expectedResponse) => {
      const callId = getEvidenceCallId(evidenceItem)
      const callType = evidenceItem.call_type || 'call'
      return executeTestWithTracking(evidenceItem.id, callId, expectedResponse, callType)
    },
    [executeTestWithTracking]
  )

  const handleRunManualTest = useCallback(
    (testCase) => {
      const callId = testCase.call_id || testCase.message_id
      return executeTestWithTracking(
        testCase.id,
        callId,
        testCase.expected_result,
        testCase.call_type
      )
    },
    [executeTestWithTracking]
  )

  const handleRunPromptTest = useCallback(
    (promptTest) => {
      const testKey = `prompt-test-${promptTest.id}`
      const callId = promptTest.inputs?.call_id || promptTest.inputs?.message_id
      const callType = promptTest.inputs?.call_type || 'call'
      return executeTestWithTracking(testKey, callId, promptTest.expected_response, callType)
    },
    [executeTestWithTracking]
  )

  const handleRunAllTests = useCallback(async () => {
    if (!prompt) {
      return
    }

    const promptTests = prompt?.prompt_tests || []
    const allItems = [...allEvidence, ...manualTestCases]
    const totalCount = allItems.length + promptTests.length

    if (totalCount === 0) {
      return
    }

    setRunAllLoading(true)
    setAllTestResults({})

    const loadingState = {
      ...allItems.reduce((acc, item) => ({ ...acc, [item.id]: true }), {}),
      ...promptTests.reduce((acc, test) => ({ ...acc, [`prompt-test-${test.id}`]: true }), {}),
    }
    setTestLoading(loadingState)

    try {
      // Run evidence + manual tests in parallel
      const evidenceAndManualPromises = allItems.map(async (item) => {
        const { isManual } = item
        const callId = isManual ? item.call_id || item.message_id : getEvidenceCallId(item)
        const callType = item.call_type || 'call'
        const itemExpected = item.expected_result || expectedScore || 'yes'
        const result = await handleRunTest(callId, itemExpected, callType)
        return { itemId: item.id, result }
      })

      // Run saved prompt tests in parallel
      const promptTestPromises = promptTests.map(async (test) => {
        const callId = test.inputs?.call_id || test.inputs?.message_id
        const callType = test.inputs?.call_type || 'call'
        const result = await handleRunTest(callId, test.expected_response, callType)
        return { itemId: `prompt-test-${test.id}`, result }
      })

      const resultsArray = await Promise.all([...evidenceAndManualPromises, ...promptTestPromises])

      const results = resultsArray.reduce((acc, { itemId, result }) => {
        if (result) {
          acc[itemId] = result
        }
        return acc
      }, {})

      setAllTestResults(results)

      const passCount = Object.values(results).filter((result) => result.responses_match).length
      const resultCount = Object.keys(results).length
      if (passCount === resultCount) {
        toast.success(`All ${resultCount} tests passed`)
      } else {
        toast.error(`${passCount}/${resultCount} tests passed`)
      }
    } finally {
      setRunAllLoading(false)
      setTestLoading({})
    }
  }, [prompt, allEvidence, manualTestCases, expectedScore, handleRunTest])

  return {
    testLoading,
    runAllLoading,
    allTestResults,
    expandedTests,
    setExpandedTests,
    handleRunSingleTest,
    handleRunAllTests,
    manualTestCases,
    addManualTestCase,
    removeManualTestCase,
    handleRunManualTest,
    handleRunPromptTest,
  }
}
